import { useMemo, useState, useEffect } from 'react'
import {
  pursuitMeta, ownerName, makeComparator, formatDate, dayKey, parseDayKey,
} from '../../../lib/championships.js'
import { SortBar } from './SortControls.jsx'
import RecordTable from './RecordTable.jsx'
import DisruptionOverlay, { DisruptionCellMarks, DisruptionDayDetail } from './DisruptionOverlay.jsx'
import { disruptionsForMonth } from '../../../lib/supplyChainDisruption.js'
import styles from './pursuit.module.css'

/* ⚠️ `dayKey` / `parseDayKey` MOVED TO lib/championships.js — NOT COPIED.
 *
 * They were defined here, and when the rep worklist needed a day key there were
 * already THREE implementations in the tree (here, reviews/dispatch.js:280,
 * reviews/reviewCoverage.js:302) — and dispatch.js's own comment says the UTC
 * fault was fixed "in the Command Center's dayKey() and AGAIN in
 * reviewCoverage.js". Writing a fourth for the worklist is how the next person
 * fixes it a fourth time.
 *
 * The rule itself is unchanged and the reason it exists is worth keeping here:
 * `YYYY-MM-DD` from LOCAL parts, never `toISOString().slice(0,10)`, because
 * that converts to UTC first and west of Greenwich an evening event keys to the
 * following day — so the panel opens on a date the grid never showed. This
 * view buckets on `getDate()`, so the key must be local too.
 *
 * ⚠️ Re-exported, because `?day=` deep links and anything else importing them
 * from this module must keep working — moving a helper is not a reason to break
 * a caller. The reviews pair is deliberately left alone: dispatch.js also
 * handles a date-only STRING, which is different semantics rather than a
 * duplicate, and merging it unverified would be consistency in the wrong
 * direction. */
export { dayKey, parseDayKey }

// The day panel is a PAGE, never a cap — the same rule the rest of the board
// follows. A day with 300 records states 300 and renders them on demand.
const DAY_PAGE = 50

// Chips inside a day cell are ordered by this key — including by rep. The name
// key is labelled with the space's own noun (an RFP is not a championship).
const sortFieldsFor = (noun, hasProduct) => [
  { key: 'name', label: noun },
  { key: 'owner', label: 'Representative' },
  { key: 'state', label: 'State' },
  ...(hasProduct ? [{ key: 'product', label: 'Product' }] : []),
  { key: 'sport', label: 'Sport' },
  { key: 'level', label: 'Level' },
  { key: 'pursuit', label: 'Status' },
]

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// Chip color by pursuit/champion status: grey=unworked, amber=in-contention,
// green=won/crowned, red=lost.
function chipTone(c) {
  const p = String(c.pursuit || '').toLowerCase()
  if (c.crowned || p === 'won' || p === 'champion crowned') return 'green'
  if (/lost|dead/.test(p)) return 'red'
  if (/outreach|contention|in progress|proposal|quote/.test(p)) return 'amber'
  return 'grey'
}

/** Month grid on event_date; chips colored by status. Chips open the detail
 * drawer + action console when `onOpen` is provided. */
export default function CountdownCalendar({
  items, now, onOpen, noun = 'Championship', selection = null, dateLabel = 'Event date',
  selectedDay = '', onSelectDay = null,
}) {
  const dated = useMemo(() => items.filter((c) => c.effDate), [items])
  const SORT_FIELDS = useMemo(
    () => sortFieldsFor(noun, items.some((c) => c.product)),
    [items, noun]
  )
  // Start on the month with the nearest upcoming event, else current month.
  const initial = useMemo(() => {
    const future = dated.filter((c) => c.effDate >= now).sort((a, b) => a.effDate - b.effDate)
    const d = future[0]?.effDate || now
    return { y: d.getFullYear(), m: d.getMonth() }
  }, [dated, now])
  const [ym, setYm] = useState(initial)
  const [sort, setSort] = useState({ field: 'name', dir: 'asc' })
  const cmp = useMemo(() => makeComparator(sort.field, sort.dir, now), [sort, now])

  /* THE SELECTED DAY IS IN THE URL, so a date is linkable — the same call the
   * Studio made for `?stage=`. The parent owns the param; this component stays
   * usable without one (uncontrolled local state) so it is not coupled to a
   * router. An unparseable value selects nothing rather than throwing. */
  const [localDay, setLocalDay] = useState('')
  const activeDay = onSelectDay ? selectedDay : localDay
  const selectDay = (key) => {
    const next = key === activeDay ? '' : key   // clicking the open day closes it
    if (onSelectDay) onSelectDay(next)
    else setLocalDay(next)
  }
  const [dayLimit, setDayLimit] = useState(DAY_PAGE)
  useEffect(() => { setDayLimit(DAY_PAGE) }, [activeDay])

  const selectedDate = useMemo(() => parseDayKey(activeDay), [activeDay])

  /* Selecting a day from a link must land on that day's MONTH. Without this a
   * shared `?day=` on another month opens the grid on the default month with a
   * panel below it for a date not on screen. */
  useEffect(() => {
    if (!selectedDate) return
    setYm((cur) => (cur.y === selectedDate.getFullYear() && cur.m === selectedDate.getMonth()
      ? cur
      : { y: selectedDate.getFullYear(), m: selectedDate.getMonth() }))
  }, [selectedDate])

  /* EVERY record on the selected date — read from the whole dated universe, not
   * from the month grid, so a linked day resolves even before the grid has been
   * paged to it. Sorted by the same comparator the chips use. */
  const dayRows = useMemo(() => {
    if (!activeDay) return []
    return dated.filter((c) => dayKey(c.effDate) === activeDay).sort(cmp)
  }, [dated, activeDay, cmp])

  const byDay = useMemo(() => {
    const m = {}
    for (const c of dated) {
      if (c.effDate.getFullYear() === ym.y && c.effDate.getMonth() === ym.m) {
        const day = c.effDate.getDate()
        ;(m[day] = m[day] || []).push(c)
      }
    }
    for (const k of Object.keys(m)) m[k].sort(cmp)
    return m
  }, [dated, ym, cmp])

  /* SUPPLY-CHAIN DISRUPTION for the month on screen. Computed once per month
   * rather than once per cell — the grid asks 42 times and the data set is
   * small, but there is no reason to pay for it 42 times.
   *
   * The disruption lib imports `dayKey` from lib/championships.js — the same
   * consolidated one this file re-exports above. No fourth copy, and no
   * injection now that the shared helper exists on main. */
  const disruptionByDay = useMemo(() => disruptionsForMonth(ym.y, ym.m), [ym])

  const monthCount = Object.values(byDay).reduce((s, a) => s + a.length, 0)
  const first = new Date(ym.y, ym.m, 1)
  const startDow = first.getDay()
  const daysInMonth = new Date(ym.y, ym.m + 1, 0).getDate()
  const cells = []
  for (let i = 0; i < startDow; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)

  const shift = (delta) => setYm(({ y, m }) => {
    const nm = m + delta
    return { y: y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 }
  })

  return (
    <div className={styles.calendar}>
      <div className={styles.calBar}>
        <button className={styles.calNav} onClick={() => shift(-1)}>‹</button>
        <div className={styles.calTitle}>{MONTHS[ym.m]} {ym.y}<span className={styles.calCount}>{monthCount.toLocaleString()} events</span></div>
        <button className={styles.calNav} onClick={() => shift(1)}>›</button>
        <SortBar sort={sort} setSort={setSort} fields={SORT_FIELDS} label="Order chips by" />
        <div className={styles.calLegend}>
          <span><i className={styles.dotGrey} />unworked</span>
          <span><i className={styles.dotAmber} />in contention</span>
          <span><i className={styles.dotGreen} />won/crowned</span>
          <span><i className={styles.dotRed} />lost</span>
        </div>
      </div>
      {/* ⭐ SUPPLY-CHAIN DISRUPTION — ABOVE the grid, not behind a toggle.
          Brian: "This should show up in command center." A rep opening the
          calendar meets this without being told where to look, and it sits
          where delivery timing already lives. */}
      <DisruptionOverlay
        monthLabel={`${MONTHS[ym.m]} ${ym.y}`}
        monthEntries={disruptionByDay}
        items={dated}
      />
      <div className={styles.calGrid}>
        {DOW.map((d) => <div key={d} className={styles.calDow}>{d}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={i} className={`${styles.calCell} ${styles.calCellEmpty}`} />
          const onDay = byDay[d] || []
          const key = dayKey(new Date(ym.y, ym.m, d))
          const isOpen = key === activeDay
          /* The day's disruption, if any. A cell carrying BOTH championships
           * and a disruption is the collision worth seeing, so it is marked as
           * such rather than just tinted. */
          const dis = disruptionByDay[d] || null
          const collides = Boolean(dis?.list?.length && onDay.length)
          /* THE WHOLE CELL IS THE CONTROL.
           *
           * Brian: "when i click on a date - i should see below every
           * championship for that date." The cell was inert and the "+N" was a
           * dead end — it stated how many were withheld and offered no way to
           * reach them. It is now the affordance rather than the apology. */
          return (
            <div
              key={i}
              /* A DISRUPTED DAY IS CLICKABLE EVEN WITH NO CHAMPIONSHIPS ON IT.
               * Otherwise the marks would be a dead end — visible, labelled,
               * and with no way to reach the dates, window or sources behind
               * them. That is the "+N more" mistake this cell already fixed
               * once, and `check-clickable` exists because of it. */
              className={`${styles.calCell} ${onDay.length ? styles.calCellDay : ''} ${isOpen ? styles.calCellOpen : ''} ${dis?.list?.length ? styles.calCellDisrupted : ''} ${collides ? styles.calCellCollide : ''}`}
              role={onDay.length || dis ? 'button' : undefined}
              tabIndex={onDay.length || dis ? 0 : undefined}
              aria-pressed={onDay.length || dis ? isOpen : undefined}
              aria-label={onDay.length || dis ? [
                `${MONTHS[ym.m]} ${d}`,
                onDay.length ? `${onDay.length} ${onDay.length === 1 ? noun.toLowerCase() : noun.toLowerCase() + 's'}` : null,
                /* The disruption is in the ACCESSIBLE NAME, not only in colour
                 * or a glyph — a screen-reader user gets the same signal. */
                dis?.list?.length
                  ? `supply-chain disruption: ${dis.list.map((h) => `${h.laneMeta.code} ${h.name} (${h.severityMeta.label})`).join('; ')}`
                  : null,
                dis?.makeup ? 'JD make-up working day — factory open' : null,
                collides ? 'championship event date falls on a disrupted date' : null,
              ].filter(Boolean).join(' — ') : undefined}
              onClick={onDay.length || dis ? () => selectDay(key) : undefined}
              onKeyDown={onDay.length || dis ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectDay(key) }
              } : undefined}
            >
              <div className={styles.calDate}>
                {d}
                {onDay.length > 0 && <span className={styles.calDayCount}>{onDay.length.toLocaleString()}</span>}
              </div>
              {/* The three overlays. Codes and glyphs, never a colour wash. */}
              <DisruptionCellMarks entry={dis} />
              {onDay.slice(0, 4).map((c) => (
                <div
                  key={c.id}
                  className={`${styles.calChip} ${onOpen ? styles.rowClickable : ''}`}
                  data-tone={chipTone(c)}
                  title={`${c.name}${ownerName(c) ? ' · ' + ownerName(c) : ''} · ${pursuitMeta(c.pursuit).label}`}
                  onClick={onOpen ? (e) => { e.stopPropagation(); onOpen(c) } : undefined}
                >
                  {c.name}
                </div>
              ))}
              {onDay.length > 4 && (
                <div className={styles.calMore}>
                  {isOpen ? 'showing all below ▾' : `+${onDay.length - 4} more — click for all ${onDay.length.toLocaleString()}`}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* ---- THE DAY PANEL — every record on the selected date ------------
          Same rows as the Worklist (RecordTable) and the same drawer
          click-through, so nothing new has to be learned or trusted. Paged,
          never capped: the header states the full count and the footer says
          exactly how many are not yet drawn. */}
      {activeDay && (
        <section className={styles.dayPanel} id="cc-day-panel">
          <div className={styles.dayHead}>
            <div>
              <span className={styles.dayTitle}>
                {selectedDate ? formatDate(selectedDate) : activeDay}
              </span>
              <span className={styles.dayCount}>
                {dayRows.length.toLocaleString()} {dayRows.length === 1 ? noun.toLowerCase() : `${noun.toLowerCase()}s`} on this date
              </span>
            </div>
            <button className={styles.dayClose} onClick={() => selectDay(activeDay)}>Close ✕</button>
          </div>

          {/* ⭐ THE DISRUPTION DETAIL — official dates, the effective window,
              severity, and the provenance for each, including every UNKNOWN
              with its reason. Rendered ABOVE the records, because if this date
              is a shutdown that is the first thing that matters about it. */}
          <DisruptionDayDetail dayKeyStr={activeDay} />

          {dayRows.length === 0 ? (
            /* Reachable from a shared link whose day holds nothing in THIS
             * space or under the current filters — so it says which, rather
             * than rendering an empty table. */
            <div className={styles.emptyState}>
              No {noun.toLowerCase()} records on this date in the current view. Clear the filters, or
              pick a date with a count on it.
            </div>
          ) : (
            <>
              <RecordTable
                rows={dayRows.slice(0, dayLimit)}
                selectAllRows={dayRows}
                now={now}
                onOpen={onOpen}
                sort={sort}
                setSort={setSort}
                selection={selection}
                noun={noun}
                dateLabel={dateLabel}
                hasProduct={items.some((c) => c.product)}
                selectAllLabel={`rows on ${selectedDate ? formatDate(selectedDate) : activeDay}`}
              />
              {dayRows.length > dayLimit && (
                <div className={styles.more}>
                  <button className={styles.moreBtn} onClick={() => setDayLimit(dayLimit + DAY_PAGE * 4)}>
                    Show {Math.min(DAY_PAGE * 4, dayRows.length - dayLimit).toLocaleString()} more
                  </button>
                  <button className={styles.moreBtn} onClick={() => setDayLimit(dayRows.length)}>
                    Show all {dayRows.length.toLocaleString()}
                  </button>
                  <span className={styles.moreNote}>
                    {(dayRows.length - dayLimit).toLocaleString()} not yet rendered — reachable, not hidden
                  </span>
                </div>
              )}
              <div className={styles.dayFoot}>
                {Math.min(dayLimit, dayRows.length).toLocaleString()} of {dayRows.length.toLocaleString()} rendered
                {' · '}this is every {noun.toLowerCase()} dated {selectedDate ? formatDate(selectedDate) : activeDay} in the current view.
              </div>
            </>
          )}
        </section>
      )}
      {monthCount === 0 && <div className={styles.emptyState}>No events this month. Dates are effective (confirmed or estimated from season + sport) — page months to find populated ones, or use the Worklist period filter.</div>}
    </div>
  )
}
